import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { DigitalCard } from '@/components/DigitalCard';
import { IdCard, ShieldCheck, UserCheck, FileCheck, ArrowRight, Plane, CheckCircle, Newspaper, Calendar } from 'lucide-react';
import { useI18n } from '@/contexts/I18nContext';
import { useTheme } from '@/contexts/ThemeContext';
import api from '@/lib/api';

const iconMap = {
  'user-check': UserCheck,
  'shield-check': ShieldCheck,
  'id-card': IdCard,
  'file-check': FileCheck,
};

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Landing() {
  const { t, lang } = useI18n();
  const { theme } = useTheme();
  const dk = theme === 'dark';
  const [cms, setCms] = useState(null);
  const [sections, setSections] = useState([]);
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    api.get('/cms/settings').then(r => setCms(r.data)).catch(() => {});
    api.get('/cms/sections').then(r => setSections(r.data)).catch(() => {});
    api.get('/blog/posts?published_only=true').then(r => setPosts(r.data)).catch(() => {});
  }, []);

  const features = cms?.features || [
    { title: 'Inscription gratuite', description: "L'enregistrement est entierement gratuit", icon: 'user-check' },
    { title: 'Validation officielle', description: 'Dossiers valides par les ambassades', icon: 'shield-check' },
    { title: 'Carte numerique', description: 'Carte avec QR code securise', icon: 'id-card' },
    { title: 'Suivi en temps reel', description: "Suivez l'etat de votre dossier", icon: 'file-check' },
  ];

  const steps = [
    { num: '01', titleKey: 'landing.step1', title: 'Creez votre compte', descKey: 'landing.step1_desc', desc: 'Inscription gratuite en quelques minutes' },
    { num: '02', titleKey: 'landing.step2', title: 'Remplissez votre dossier', descKey: 'landing.step2_desc', desc: 'Informations personnelles et documents' },
    { num: '03', titleKey: 'landing.step3', title: 'Validation ambassade', descKey: 'landing.step3_desc', desc: 'Votre dossier est verifie par l\'ambassade' },
    { num: '04', titleKey: 'landing.step4', title: 'Recevez votre carte', descKey: 'landing.step4_desc', desc: 'Carte numerique avec QR code securise' },
  ];

  const demoCard = {
    full_name: 'Jean-Pierre Mukeba',
    date_of_birth: '15/03/1990',
    nationality: 'Congolaise',
    card_number: 'CE-2026-000001',
    country_of_residence: 'France',
    expires_at: '2031-02-01T00:00:00',
    qr_code_data: null,
  };

  // Determine which sections to show and in which order
  const visibleSections = sections.length > 0
    ? sections.filter(s => s.is_visible)
    : [
        { section_id: 'hero', type: 'hero' },
        { section_id: 'features', type: 'features' },
        { section_id: 'steps', type: 'steps' },
        { section_id: 'about', type: 'about' },
        { section_id: 'news', type: 'news' },
        { section_id: 'cta', type: 'cta' },
      ];

  const renderSection = (section) => {
    switch (section.type) {
      case 'hero': return <HeroSection key="hero" cms={cms} t={t} demoCard={demoCard} dk={dk} />;
      case 'features': return <FeaturesSection key="features" features={features} t={t} dk={dk} />;
      case 'steps': return <StepsSection key="steps" steps={steps} t={t} dk={dk} />;
      case 'about': return <AboutSection key="about" cms={cms} t={t} dk={dk} />;
      case 'news': return posts.length > 0 ? <NewsSection key="news" posts={posts} lang={lang} t={t} dk={dk} /> : null;
      case 'cta': return <CtaSection key="cta" t={t} dk={dk} />;
      case 'custom': return <CustomSection key={section.section_id} section={section} dk={dk} />;
      default: return <CustomSection key={section.section_id} section={section} dk={dk} />;
    }
  };

  return <div>{visibleSections.map(renderSection)}</div>;
}

function HeroSection({ cms, t, demoCard, dk }) {
  return (
    <section className={`relative overflow-hidden ${dk ? 'bg-[#0B1120]' : ''}`} data-testid="hero-section">
      <div className={`absolute inset-0 ${dk ? 'bg-[#0B1120]' : 'bg-[#F8FAFC]'}`}>
        <div className="absolute top-0 right-0 w-1/2 h-full opacity-5"
          style={{ backgroundImage: 'radial-gradient(circle at 70% 50%, #007FFF 0%, transparent 70%)' }} />
      </div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 lg:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="animate-fade-in">
            <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-6 ${dk ? 'bg-[#007FFF]/20' : 'bg-[#007FFF]/10'}`}>
              <Plane className="w-4 h-4 text-[#007FFF]" />
              <span className="text-sm font-medium text-[#007FFF]">{t('landing.official')}</span>
            </div>
            <h1 className={`text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight ${dk ? 'text-white' : 'text-[#0F172A]'}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
              {cms?.hero_title || t('landing.hero_title')}
            </h1>
            <p className={`mt-6 text-base md:text-lg max-w-lg leading-relaxed ${dk ? 'text-gray-400' : 'text-[#475569]'}`}>
              {cms?.hero_subtitle || t('landing.hero_subtitle')}
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/register">
                <Button size="lg" className="bg-[#007FFF] hover:bg-[#0066CC] text-white px-8 h-12 text-base" data-testid="hero-register-btn">
                  {t('landing.start_registration')} <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="outline" className={`h-12 text-base px-8 ${dk ? 'border-gray-600 text-gray-300 hover:bg-gray-800 hover:text-white' : ''}`} data-testid="hero-login-btn">
                  {t('nav.login')}
                </Button>
              </Link>
            </div>
            <div className={`mt-8 flex items-center gap-6 text-sm ${dk ? 'text-gray-400' : 'text-[#475569]'}`}>
              <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#009543]" /><span>{t('landing.free')}</span></div>
              <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#009543]" /><span>{t('landing.secure')}</span></div>
              <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#009543]" /><span>{t('landing.official_short')}</span></div>
            </div>
          </div>
          <div className="animate-fade-in-delay-2 flex justify-center">
            <div className="transform rotate-2 hover:rotate-0 transition-transform duration-500">
              <DigitalCard card={demoCard} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FeaturesSection({ features, t, dk }) {
  return (
    <section className={`py-16 md:py-24 ${dk ? 'bg-[#111827]' : 'bg-white'}`} data-testid="features-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className={`text-3xl sm:text-4xl font-bold ${dk ? 'text-white' : 'text-[#0F172A]'}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
            {t('landing.why_card')}
          </h2>
          <p className={`mt-3 text-base md:text-lg max-w-2xl mx-auto ${dk ? 'text-gray-400' : 'text-[#475569]'}`}>
            {t('landing.service_desc')}
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, i) => {
            const Icon = iconMap[feature.icon] || IdCard;
            return (
              <Card key={i} className={`border hover:shadow-md transition-shadow animate-fade-in ${dk ? 'bg-[#1E293B] border-gray-700' : 'border-border'}`} style={{ animationDelay: `${i * 100}ms` }}>
                <CardContent className="p-6">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${dk ? 'bg-[#007FFF]/20' : 'bg-[#007FFF]/10'}`}>
                    <Icon className="w-6 h-6 text-[#007FFF]" />
                  </div>
                  <h3 className={`text-base font-semibold mb-2 ${dk ? 'text-white' : ''}`} style={{ fontFamily: 'Outfit, sans-serif' }}>{feature.title}</h3>
                  <p className={`text-sm ${dk ? 'text-gray-400' : 'text-[#475569]'}`}>{feature.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function StepsSection({ steps, t, dk }) {
  return (
    <section className={`py-16 md:py-24 ${dk ? 'bg-[#0B1120]' : 'bg-[#F8FAFC]'}`} data-testid="steps-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className={`text-3xl sm:text-4xl font-bold ${dk ? 'text-white' : 'text-[#0F172A]'}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
            {t('landing.how_it_works')}
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((step, i) => (
            <div key={i} className="relative animate-fade-in" style={{ animationDelay: `${i * 100}ms` }}>
              <div className={`rounded-xl p-6 border hover:shadow-md transition-shadow h-full ${dk ? 'bg-[#1E293B] border-gray-700' : 'bg-white border-border'}`}>
                <span className={`text-4xl font-extrabold ${dk ? 'text-[#007FFF]/25' : 'text-[#007FFF]/15'}`} style={{ fontFamily: 'Outfit, sans-serif' }}>{step.num}</span>
                <h3 className={`text-base font-semibold mt-2 mb-2 ${dk ? 'text-white' : ''}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {t(step.titleKey) !== step.titleKey ? t(step.titleKey) : step.title}
                </h3>
                <p className={`text-sm ${dk ? 'text-gray-400' : 'text-[#475569]'}`}>
                  {t(step.descKey) !== step.descKey ? t(step.descKey) : step.desc}
                </p>
              </div>
              {i < steps.length - 1 && (
                <div className="hidden lg:block absolute top-1/2 -right-3 w-6 text-[#007FFF]/30">
                  <ArrowRight className="w-6 h-6" />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AboutSection({ cms, t, dk }) {
  return (
    <section id="about" className={`py-16 md:py-24 ${dk ? 'bg-[#111827]' : 'bg-white'}`} data-testid="about-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className={`text-3xl sm:text-4xl font-bold mb-6 ${dk ? 'text-white' : 'text-[#0F172A]'}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
              {t('landing.about_title')}
            </h2>
            <p className={`text-base leading-relaxed ${dk ? 'text-gray-400' : 'text-[#475569]'}`}>
              {cms?.about_text || t('landing.about_text')}
            </p>
            <div className="mt-8">
              <Link to="/register">
                <Button size="lg" className="bg-[#009543] hover:bg-[#007A36] text-white" data-testid="about-cta-btn">
                  {t('landing.register_now')} <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
          <div className="rounded-2xl overflow-hidden shadow-lg">
            <img
              src="https://images.unsplash.com/photo-1561451518-e77b834887ed?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwzfHxsdXNoJTIwZ3JlZW4lMjBhZnJpY2FuJTIwbGFuZHNjYXBlJTIwcml2ZXJ8ZW58MHx8fHwxNzczNDE5MzA2fDA&ixlib=rb-4.1.0&q=85&w=800"
              alt="Congo landscape"
              className="w-full h-64 lg:h-80 object-cover"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function NewsSection({ posts, lang, t, dk }) {
  const filteredPosts = posts.filter(p => p.lang === lang || !p.lang).slice(0, 3);
  if (filteredPosts.length === 0) return null;

  return (
    <section className={`py-16 md:py-24 ${dk ? 'bg-[#0B1120]' : 'bg-[#F8FAFC]'}`} data-testid="news-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className={`text-3xl sm:text-4xl font-bold ${dk ? 'text-white' : 'text-[#0F172A]'}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
            {t('landing.news_title')}
          </h2>
          <p className={`mt-3 text-base md:text-lg max-w-2xl mx-auto ${dk ? 'text-gray-400' : 'text-[#475569]'}`}>
            {t('landing.news_desc')}
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPosts.map((post, i) => (
            <Card key={post.post_id} className={`border hover:shadow-md transition-shadow animate-fade-in overflow-hidden ${dk ? 'bg-[#1E293B] border-gray-700' : 'border-border'}`} style={{ animationDelay: `${i * 100}ms` }}>
              {post.image_url && (
                <div className="h-48 overflow-hidden">
                  <img
                    src={post.image_url.startsWith('http') ? post.image_url : `${BACKEND}${post.image_url}`}
                    alt={post.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <CardContent className="p-5">
                <div className={`flex items-center gap-2 text-xs mb-3 ${dk ? 'text-gray-500' : 'text-muted-foreground'}`}>
                  <Calendar className="w-3.5 h-3.5" />
                  <span>{post.created_at ? new Date(post.created_at).toLocaleDateString('fr-FR') : ''}</span>
                  {post.author_name && <span>- {post.author_name}</span>}
                </div>
                <h3 className={`text-base font-semibold mb-2 line-clamp-2 ${dk ? 'text-white' : ''}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {post.title}
                </h3>
                <p className={`text-sm line-clamp-3 ${dk ? 'text-gray-400' : 'text-[#475569]'}`}>
                  {post.excerpt || post.content?.substring(0, 150)}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

function CtaSection({ t, dk }) {
  return (
    <section className="py-16 md:py-20 bg-[#007FFF]" data-testid="cta-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {t('landing.cta_title')}
        </h2>
        <p className="text-base md:text-lg text-white/80 mb-8">
          {t('landing.cta_desc')}
        </p>
        <Link to="/register">
          <Button size="lg" className="bg-white text-[#007FFF] hover:bg-gray-100 h-12 px-10 text-base font-semibold" data-testid="final-cta-btn">
            {t('landing.start_now')} <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </Link>
      </div>
    </section>
  );
}


function CustomSection({ section, dk }) {
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const imgUrl = section.image_url
    ? (section.image_url.startsWith('/') ? `${BACKEND}${section.image_url}` : section.image_url)
    : '';
  const imgLeft = section.image_position === 'left';
  const hasImage = !!imgUrl;
  const btnUrl = section.button_url || '';
  const isInternal = btnUrl.startsWith('/') || btnUrl.startsWith('#');

  return (
    <section className={`py-16 md:py-24 ${dk ? 'bg-[#111827]' : 'bg-white'}`} data-testid={`custom-section-${section.section_id}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`grid grid-cols-1 ${hasImage ? 'lg:grid-cols-2' : ''} gap-10 lg:gap-16 items-center`}>
          {hasImage && imgLeft && (
            <div className="animate-fade-in">
              <img src={imgUrl} alt={section.title || ''} className="rounded-xl shadow-lg w-full max-h-[420px] object-cover" loading="lazy" />
            </div>
          )}
          <div className={`animate-fade-in ${hasImage ? '' : 'max-w-3xl mx-auto text-center'}`}>
            {section.title && (
              <h2 className={`text-3xl sm:text-4xl font-bold mb-4 ${dk ? 'text-white' : 'text-[#0F172A]'}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                {section.title}
              </h2>
            )}
            {section.subtitle && (
              <p className={`text-base md:text-lg mb-4 ${dk ? 'text-gray-400' : 'text-[#475569]'}`}>{section.subtitle}</p>
            )}
            {section.content && (
              <div className={`prose prose-sm max-w-none ${dk ? 'prose-invert' : ''} ${dk ? 'text-gray-300' : 'text-[#475569]'}`}
                dangerouslySetInnerHTML={{ __html: section.content }} />
            )}
            {section.button_label && btnUrl && (
              <div className="mt-6">
                {isInternal ? (
                  <Link to={btnUrl}>
                    <Button size="lg" className="bg-[#007FFF] hover:bg-[#0066CC] text-white px-8 h-12 text-base">
                      {section.button_label} <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </Link>
                ) : (
                  <a href={btnUrl} target="_blank" rel="noopener noreferrer">
                    <Button size="lg" className="bg-[#007FFF] hover:bg-[#0066CC] text-white px-8 h-12 text-base">
                      {section.button_label} <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </a>
                )}
              </div>
            )}
          </div>
          {hasImage && !imgLeft && (
            <div className="animate-fade-in">
              <img src={imgUrl} alt={section.title || ''} className="rounded-xl shadow-lg w-full max-h-[420px] object-cover" loading="lazy" />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
